import React from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { makeStyles, Theme } from '@material-ui/core/styles';

import ChoiceSelector from '../../../../../../components/ImpactAiDrawer/components/ChoiceSelector';
import { ImpactAiContextProvider } from '../../../../../../components/ImpactAiDrawer/hooks/ImpactAiContextProvider';

import { ChoiceOption } from './types';

interface PreviewSectionProps {
  choices: Record<string, ChoiceOption>;
}

// Replicate the chat styles for proper preview context
const usePreviewStyles = makeStyles((theme: Theme) => ({
  chatPreviewContainer: {
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    minHeight: 150,
    maxWidth: 600,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  messageWrapper: {
    display: 'flex',
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  botMessage: {
    justifyContent: 'flex-start',
  },
  botMessageBubble: {
    backgroundColor: '#F4F4F6',
    color: theme.palette.text.primary,
    border: 'none',
    borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(2)}px 0`,
    padding: theme.spacing(1, 2),
    display: 'inline-block',
    maxWidth: '80%',
    wordBreak: 'break-word',
  },
  // Inline choice selector styles from the actual chat
  inlineChoiceWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    margin: '0 auto',
    maxWidth: '90%',
    position: 'relative',
  },
  inlineChoiceContainer: {
    padding: `${theme.spacing(1)}px 0`,
    maxWidth: '100%',
    position: 'relative',
  },
}));

const PreviewSection: React.FC<PreviewSectionProps> = ({ choices }) => {
  const classes = usePreviewStyles();

  const hasChoices = Object.keys(choices).length > 0;
  const hasValidChoices = Object.values(choices).every((choice) => choice.text && choice.value);

  if (!hasChoices) {
    return <Alert severity="info">Add choices to see a preview</Alert>;
  }

  if (!hasValidChoices) {
    return (
      <Alert severity="warning">
        All choices must have both display text and value to show preview
      </Alert>
    );
  }

  // Convert our internal ChoiceOption to the format expected by ChoiceSelector
  const formattedChoices = Object.entries(choices).reduce((acc, [key, choice]) => {
    acc[key] = {
      text: choice.text,
      value: choice.value,
      ...(choice.backgroundColor && { backgroundColor: choice.backgroundColor }),
      ...(choice.textColor && { textColor: choice.textColor }),
      ...(choice.icon && { icon: choice.icon }),
      ...(choice.iconColor && { iconColor: choice.iconColor }),
    };
    return acc;
  }, {} as Record<string, ChoiceOption>);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Preview
      </Typography>

      <Paper elevation={2} className={classes.chatPreviewContainer}>
        {/* Choice selector in inline mode */}
        <div className={classes.messageWrapper}>
          <div className={classes.inlineChoiceWrapper}>
            <ImpactAiContextProvider initialContext={{}}>
              <ChoiceSelector
                choices={formattedChoices}
                onSelect={(key, choice) => {
                  // eslint-disable-next-line no-console
                  console.log('Preview selection:', key, choice);
                }}
                className={classes.inlineChoiceContainer}
                inline
              />
            </ImpactAiContextProvider>
          </div>
        </div>
      </Paper>

      <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
        This is how choices appear in the chat interface
      </Typography>
    </Box>
  );
};

export default PreviewSection;
